/**
 * Invoice View Helpers
 *
 * Utility functions, constants, and types shared across InvoiceView sub-components.
 */

import type { Address, Invoice, InvoiceStatus } from '../../../types/billing'

// =============================================================================
// Types
// =============================================================================

/** Legacy flat address fields that may exist on older invoice records */
export interface InvoiceLegacyFields {
  shipToAddressLine1?: string
  shipToAddressLine2?: string
  shipToCity?: string
  shipToState?: string
  shipToPostalCode?: string
  shipToCountry?: string
  consigneeAddressLine1?: string
  consigneeAddressLine2?: string
  consigneeCity?: string
  consigneeState?: string
  consigneePostalCode?: string
  consigneeCountry?: string
}

export interface TaxBreakdownEntry {
  rate: number
  cgst: number
  sgst: number
  igst: number
}

// =============================================================================
// Constants
// =============================================================================

export const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  posted: 'Posted',
  voided: 'Voided',
  cancelled: 'Cancelled',
}

export const STATUS_COLORS: Record<InvoiceStatus, { bg: string; text: string }> = {
  draft: { bg: '#e9ecef', text: '#495057' },
  posted: { bg: '#cfe2ff', text: '#084298' },
  voided: { bg: '#f8d7da', text: '#842029' },
  cancelled: { bg: '#e9ecef', text: '#495057' },
}

export const COUNTRY_NAMES: Record<string, string> = {
  'IN': 'India',
  'US': 'United States',
  'GB': 'United Kingdom',
  'AE': 'United Arab Emirates',
  'SG': 'Singapore',
  'AU': 'Australia',
  'CA': 'Canada',
  'DE': 'Germany',
  'FR': 'France',
  'JP': 'Japan',
  'CN': 'China',
}

export const STATE_NAMES: Record<string, string> = {
  'IN-AN': 'Andaman and Nicobar Islands',
  'IN-AP': 'Andhra Pradesh',
  'IN-AR': 'Arunachal Pradesh',
  'IN-AS': 'Assam',
  'IN-BR': 'Bihar',
  'IN-CH': 'Chandigarh',
  'IN-CT': 'Chhattisgarh',
  'IN-DN': 'Dadra and Nagar Haveli',
  'IN-DD': 'Daman and Diu',
  'IN-DL': 'Delhi',
  'IN-GA': 'Goa',
  'IN-GJ': 'Gujarat',
  'IN-HR': 'Haryana',
  'IN-HP': 'Himachal Pradesh',
  'IN-JK': 'Jammu and Kashmir',
  'IN-JH': 'Jharkhand',
  'IN-KA': 'Karnataka',
  'IN-KL': 'Kerala',
  'IN-LA': 'Ladakh',
  'IN-LD': 'Lakshadweep',
  'IN-MP': 'Madhya Pradesh',
  'IN-MH': 'Maharashtra',
  'IN-MN': 'Manipur',
  'IN-ML': 'Meghalaya',
  'IN-MZ': 'Mizoram',
  'IN-NL': 'Nagaland',
  'IN-OR': 'Odisha',
  'IN-PY': 'Puducherry',
  'IN-PB': 'Punjab',
  'IN-RJ': 'Rajasthan',
  'IN-SK': 'Sikkim',
  'IN-TN': 'Tamil Nadu',
  'IN-TG': 'Telangana',
  'IN-TR': 'Tripura',
  'IN-UP': 'Uttar Pradesh',
  'IN-UT': 'Uttarakhand',
  'IN-WB': 'West Bengal',
}

// =============================================================================
// Helper Functions
// =============================================================================

/** Format a date string to dd-Mon-yyyy in en-IN locale */
export function formatDate(dateStr: string | undefined): string {
  if (!dateStr) { return '-' }
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/** Convert a country ISO code to its full name */
export function getCountryName(code: string | undefined): string {
  if (!code) { return '' }
  return COUNTRY_NAMES[code] ?? code
}

/** Convert an Indian state code (e.g. IN-KL) to its full name */
export function getStateName(code: string | undefined): string {
  if (!code) { return '' }
  return STATE_NAMES[code] ?? code
}

/** Derive the human-readable supply type label from invoice.supplyType */
export function getSupplyTypeLabel(supplyType: string | undefined): string | undefined {
  const normalized = supplyType?.toLowerCase()
  switch (normalized) {
    case 'intrastate':
    case 'supply_type_intrastate':
      return 'Intrastate (CGST + SGST)'
    case 'interstate':
    case 'supply_type_interstate':
      return 'Interstate (IGST)'
    case 'export':
    case 'supply_type_export':
      return 'Export (Zero-rated)'
    case 'sez':
    case 'supply_type_sez':
      return 'SEZ Supply'
    default:
      if (supplyType?.startsWith('SUPPLY_TYPE_')) {
        return supplyType
          .replace('SUPPLY_TYPE_', '')
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase())
      }
      return supplyType
  }
}

/** Derive whether the invoice supply type is intrastate */
export function getIsIntrastate(supplyType: string | undefined): boolean {
  const normalized = supplyType?.toLowerCase()
  return normalized === 'intrastate' || normalized === 'supply_type_intrastate'
}

/** Derive the document label (Invoice / Bill / Credit Note / etc.) */
export function getDocLabel(invoiceType: Invoice['invoiceType']): string {
  const isVendor = invoiceType === 'vendor_bill' || invoiceType === 'vendor_credit_note'
  const isCreditNote = invoiceType === 'vendor_credit_note' || invoiceType === 'credit_note'
  if (isVendor) {
    return isCreditNote ? 'Vendor Credit Note' : 'Bill'
  }
  return isCreditNote ? 'Credit Note' : 'Invoice'
}

/** Derive the printable document heading */
export function getDocHeading(invoiceType: Invoice['invoiceType']): string {
  const isVendor = invoiceType === 'vendor_bill' || invoiceType === 'vendor_credit_note'
  const isCreditNote = invoiceType === 'vendor_credit_note' || invoiceType === 'credit_note'
  const isExport = invoiceType === 'export'
  if (isVendor) {
    return isCreditNote ? 'VENDOR CREDIT NOTE' : 'VENDOR BILL'
  }
  return isExport ? 'EXPORT INVOICE' : 'TAX INVOICE'
}

/** Build a single-line city/state/postal fragment for display */
export function formatCityStateLine(
  city: string | undefined,
  state: string | undefined,
  postalCode: string | undefined,
): string {
  const parts: string[] = []
  if (city) { parts.push(city) }
  if (state) { parts.push((city ? ', ' : '') + getStateName(state)) }
  if (postalCode) { parts.push(((city ?? state) ? ' ' : '') + postalCode) }
  return parts.join('')
}

/** Compute tax breakdown grouped by GST rate, sorted ascending */
export function computeTaxBreakdown(
  items: Invoice['items'],
  isIntrastate: boolean,
): TaxBreakdownEntry[] {
  if (!items) { return [] }
  const byRate = new Map<number, { cgst: number; sgst: number; igst: number }>()
  items.forEach((item) => {
    const rate = isIntrastate
      ? (parseFloat(String(item.cgstRate)) || 0) + (parseFloat(String(item.sgstRate)) || 0)
      : (parseFloat(String(item.igstRate)) || 0)
    if (rate === 0) { return }
    const existing = byRate.get(rate) ?? { cgst: 0, sgst: 0, igst: 0 }
    existing.cgst += Number(item.cgstAmount) || 0
    existing.sgst += Number(item.sgstAmount) || 0
    existing.igst += Number(item.igstAmount) || 0
    byRate.set(rate, existing)
  })
  return [...byRate.entries()]
    .filter(([, amounts]) => amounts.cgst > 0 || amounts.sgst > 0 || amounts.igst > 0)
    .sort(([a], [b]) => a - b)
    .map(([rate, amounts]) => ({ rate, ...amounts }))
}

/** Resolve the seller address from structured or flat fields */
export function resolveSellerAddress(invoice: Invoice): Address | null {
  if (invoice.sellerBillingAddress && typeof invoice.sellerBillingAddress === 'object') {
    return invoice.sellerBillingAddress
  }
  if (invoice.sellerAddressLine1 !== undefined || invoice.sellerCity !== undefined) {
    return {
      line1: invoice.sellerAddressLine1 ?? '',
      line2: invoice.sellerAddressLine2 ?? '',
      city: invoice.sellerCity ?? '',
      state: invoice.sellerState,
      stateCode: invoice.sellerStateCode,
      postalCode: invoice.sellerPostalCode ?? '',
      country: invoice.sellerCountry ?? '',
      countryCode: '',
    }
  }
  return null
}

function buildPartyAddressFromFlatFields(invoice: Invoice): Address {
  return {
    line1: invoice.partyAddressLine1 ?? '',
    line2: invoice.partyAddressLine2 ?? '',
    city: invoice.partyCity ?? '',
    state: invoice.partyState ?? '',
    stateCode: invoice.partyStateCode ?? '',
    postalCode: invoice.partyPostalCode ?? '',
    country: invoice.partyCountry ?? '',
    countryCode: invoice.partyCountryCode ?? '',
  }
}

/** Resolve the party billing address from structured or flat fields */
export function resolvePartyBillingAddress(invoice: Invoice): Address | null {
  if (invoice.partyBillingAddress && typeof invoice.partyBillingAddress === 'object') {
    return invoice.partyBillingAddress
  }
  if (invoice.partyAddress && typeof invoice.partyAddress === 'object') {
    return invoice.partyAddress
  }
  if (invoice.partyAddressLine1 !== undefined || invoice.partyCity !== undefined) {
    return buildPartyAddressFromFlatFields(invoice)
  }
  return null
}
